import { Capacitor } from '@capacitor/core';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import {
  checkBiometry,
  confirmIdentity,
  markBiometricsAsked,
  setBiometricsEnabled,
  shouldAskBiometrics,
} from '@/lib/biometrics';

/**
 * Предложение включить вход по лицу — один раз, после первого входа.
 *
 * ЗАЧЕМ СПРАШИВАТЬ САМИМ. Переключатель в профиле был и раньше, но про него
 * никто не знал: человек заходит учиться, а не изучать настройки. Заказчик
 * 22.08.2026 сказал прямо — «может, будем сразу спрашивать, нужен Face ID или
 * нет». Вопрос в нужный момент честнее спрятанной галочки.
 *
 * ПРАВИЛА, КОТОРЫЕ ЗДЕСЬ СОБЛЮДАЮТСЯ:
 *   • спрашиваем только там, где это работает — в приложении и при наличии
 *     биометрии на устройстве; в браузере окно не появляется вовсе;
 *   • «Не сейчас» — полноценный ответ: запоминаем и больше не пристаём;
 *   • включаем только после УСПЕШНОЙ проверки. Иначе человек включил бы защиту,
 *     не убедившись, что она срабатывает, и узнал бы об этом при следующем
 *     запуске — уже будучи запертым снаружи;
 *   • передумать можно в профиле, там тот же переключатель.
 */
export function BiometricPrompt() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!shouldAskBiometrics()) return;
    let alive = true;
    void checkBiometry().then((info) => {
      if (!alive) return;
      if (!info.available) {
        // Биометрии на устройстве нет — не спрашиваем и не возвращаемся к этому.
        markBiometricsAsked();
        return;
      }
      setName(info.name);
      setOpen(true);
    });
    return () => {
      alive = false;
    };
  }, []);

  const close = () => {
    markBiometricsAsked();
    setOpen(false);
  };

  const enable = async () => {
    setBusy(true);
    try {
      const passed = await confirmIdentity(t('biometrics.reason'), t('biometrics.usePassword'));
      if (passed) setBiometricsEnabled(true);
    } finally {
      setBusy(false);
      close();
    }
  };

  // ⚠️ На Android «Face ID» не бывает: там отпечаток или лицо, и заголовок нейтральный (техдолг п. 235).
  // Имя способа там не подставляем: `name` — номер из перечисления плагина, а не слово, и текст вышел
  // бы «достаточно 3». Ветка iOS прежняя — 56 на ревью Apple.
  const android = Capacitor.getPlatform() === 'android';

  return (
    <Modal
      open={open}
      onClose={close}
      title={android ? t('biometrics.askTitleAndroid') : t('biometrics.askTitle')}
      size="sm"
    >
      <p className="text-sm text-on-surface-variant">
        {name && !android ? t('biometrics.askDescNamed', { name }) : t('biometrics.askDesc')}
      </p>
      <p className="mt-2 text-xs text-on-surface-variant">{t('biometrics.askHint')}</p>
      <div className="mt-6 flex justify-end gap-2">
        <Button variant="ghost" onClick={close} disabled={busy}>
          {t('biometrics.askLater')}
        </Button>
        <Button onClick={() => void enable()} loading={busy}>
          {t('biometrics.askEnable')}
        </Button>
      </div>
    </Modal>
  );
}
